import { Alert, Button, Collapse, InputNumber, Layout, Skeleton, Table, Tag, Tooltip, Select, Popconfirm } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { uniqBy } from 'lodash';
import { Utils } from '../../../common/services/Utils';
import LayoutHeader from '../../../../components/LayoutHeader';
import { TestProjectPackageActions } from '..';
import { TestProjectDashboardStore, TestProjectDataStore } from '../../stores';
import { TestRunTopic } from '../../types';
import { ColumnType } from 'antd/lib/table';
import TestProjectSortingActions from '../TestProjectSortingActions/TestProjectSortingActions';
import { Constants } from '../../misc';
import TextArea from 'antd/lib/input/TextArea';
import { CopyOutlined, WarningOutlined } from '@ant-design/icons';
import './TestProjectRunResultsList.less';

type Props = {
    store: TestProjectDashboardStore;
    testProjectDataStore: TestProjectDataStore;
};

const TestProjectRunResultList: React.FC<Props> = ({ store, testProjectDataStore }) => {
    const [packageSortDirection, setPackageSortDirection] = React.useState<'ascend' | 'descend'>('ascend');
    const [packageSortField, setPackageSortField] = React.useState<string>(Constants.PACKAGE_SORT_FIELD_NAME);
    const [isEditModeOn, setIsEditModeOn] = React.useState(false);
    const [openPanelKeys, setOpenPanelKeys] = React.useState<string[]>([]);
    const [resultFilter, setResultFilter] = React.useState<'all' | 'failed'>('all');

    // Calculate failed package IDs - moved from store to component
    const getFailedPackageIds = React.useCallback(() => {
        if (!store.currentTestRunResults) {
            return [];
        }

        // Get all unique packages and check which ones have failed topics
        const uniquePackages = uniqBy(store.currentTestRunResults.topics, 'packageId');

        return uniquePackages
            .filter(pkg => {
                // Check if this package has any failed topics (same logic as red highlighting)
                const hasFailedTopics = store
                    .currentTestRunResults!.topics.filter(t => t.packageId === pkg.packageId)
                    .some(t => t.fuzzy < t.baselineFuzzy);
                return hasFailedTopics;
            })
            .map(pkg => pkg.packageId)
            .filter((id): id is string => id !== null); // Filter out null values and type guard
    }, [store.currentTestRunResults]);

    // Render batch reparse button
    const renderBatchReparseButton = React.useCallback(() => {
        const failedPackageIds = getFailedPackageIds();
        const hasFailedPackages = failedPackageIds.length > 0;
        const hasAnyBusyFailedPackages = failedPackageIds.some(id => store.busyPackages.includes(id));
        const isDisabled = !hasFailedPackages || hasAnyBusyFailedPackages;

        return (
            <Popconfirm
                key="batch-reparse-popconfirm"
                title={`Are you sure you want to reparse ${failedPackageIds.length} failed document(s)? This will process the documents again.`}
                onConfirm={() => store.reparsePackagesBatch(failedPackageIds)}
                okText="Yes"
                cancelText="No"
                placement="topLeft"
                disabled={isDisabled}
            >
                <Button
                    key="test-projects-batch-reparse"
                    data-id="button-batch-reparse"
                    size="large"
                    type="primary"
                    disabled={isDisabled}
                >
                    Reparse Failed Documents ({failedPackageIds.length})
                </Button>
            </Popconfirm>
        );
    }, [getFailedPackageIds, store]);

    React.useEffect(() => {
        if (store.currentTestRunId) {
            store.loadTestRunResults();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.currentTestRunId, testProjectDataStore]);

    React.useEffect(() => {
        if (testProjectDataStore.projectStatus === 'idle') {
            testProjectDataStore.loadTestProject();
        }

        if (
            !store.packages.length ||
            store.packages.some(document => document.project.id !== testProjectDataStore.projectId)
        ) {
            testProjectDataStore.testProjectsStore.loadAllProjectPackages();
        }
    }, [store, testProjectDataStore]);

    const packageListItems = React.useMemo(() => {
        const testResults = store.currentTestRunResults ? store.currentTestRunResults.topics.slice() : [];

        const uniqField = testResults.every(t => t.packageId != null) ? 'packageId' : 'packageName';
        let items = uniqBy(testResults, uniqField).map(t => ({ packageId: t.packageId, packageName: t.packageName }));

        if (resultFilter === 'failed') {
            items = items.filter(item => {
                const packageTopics = testResults.filter(topic =>
                    item.packageId ? topic.packageId === item.packageId : topic.packageName === item.packageName
                );
                return packageTopics.some(topic => topic.fuzzy < topic.baselineFuzzy);
            });
        }

        if (packageSortField === Constants.PACKAGE_SORT_FIELD_NAME) {
            items = items.sort((a, b) => a.packageName.localeCompare(b.packageName));
        }

        // Don't sort in case sort by date is needed, because package ids are stored in the same order as they were loaded

        if (packageSortDirection === 'descend') {
            items = items.reverse();
        }

        return items;
    }, [store.currentTestRunResults, packageSortField, packageSortDirection, resultFilter]);

    if (!store.currentTestRunResults) {
        return null;
    }

    const getTitle = () => {
        if (store.testResultIsLoading || !testProjectDataStore.testProject?.name || !store.currentTestRunResults) {
            return <Skeleton active paragraph={{ rows: 0 }} />;
        }

        return `${testProjectDataStore.testProject?.name} - ${Utils.formatDateStringShort(store.currentTestRunResults.runTime)} run results`;
    };

    const loadingContent = <Skeleton active paragraph={{ rows: 4 }} title={{ style: { display: 'none' } }} />;

    const getRunResultButtons = (topic: TestRunTopic) => {
        const viewInDocumentButton = (
            <Tooltip title="View results in document">
                <Button
                    type="link"
                    icon={
                        <i
                            className="alpha-icon xs form-anchor"
                            style={{ backgroundSize: 'contain', verticalAlign: 'middle' }}
                        />
                    }
                    onClick={() =>
                        store.openPreviewTopicResultPreviewWindow(
                            topic.packageId,
                            `${topic.packageId}-${topic.name}`,
                            topic.metaFields
                        )
                    }
                />
            </Tooltip>
        );

        return (
            <div className="topic-result-actions">
                <Tooltip title="Copy result to clipboard">
                    <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyResultToClipboard(topic.result)}
                    />
                </Tooltip>
                {topic.metaFields != null && topic.metaFields.length > 0 && viewInDocumentButton}
            </div>
        );
    };

    const copyResultToClipboard = (result: string) => {
        navigator.clipboard.writeText(result);
    };

    const handleFocus = (
        e: React.FocusEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLInputElement>,
        pkg: string
    ) => {
        const currentPackageIndex = packageListItems.findIndex(p => p.packageName === pkg || p.packageId === pkg);

        if (
            currentPackageIndex === -1 ||
            packageListItems.length === 0 ||
            currentPackageIndex === packageListItems.length - 1
        ) {
            return;
        }

        const nextPackage =
            currentPackageIndex === packageListItems.length - 1
                ? packageListItems[0]
                : packageListItems[currentPackageIndex + 1];

        if (openPanelKeys.includes(nextPackage.packageId ?? nextPackage.packageName)) {
            return;
        }

        setOpenPanelKeys(prev => [...prev, nextPackage.packageId ?? nextPackage.packageName]);
    };

    const getBaselineCellDisplayContent = (topic: TestRunTopic) => {
        if (topic.modifiedBaseline) {
            return (
                <span style={{ whiteSpace: 'pre-wrap' }}>
                    <strong>Original value: </strong>
                    {topic.baseline}
                    <br />
                    <strong>New value: </strong>
                    {topic.modifiedBaseline}
                </span>
            );
        }

        return <span style={{ whiteSpace: 'pre-wrap' }}>{topic.baseline}</span>;
    };

    const getBaselineCellContent = (topic: TestRunTopic, tabIndex: number, isLast: boolean) => {
        if (!isEditModeOn) {
            return getBaselineCellDisplayContent(topic);
        }

        const focusHandler = isLast
            ? (e: React.FocusEvent<HTMLTextAreaElement>) => handleFocus(e, topic.packageId ?? topic.packageName)
            : undefined;

        const blurHandler = (e: React.FocusEvent<HTMLTextAreaElement>) => {
            if (topic.modifiedBaseline === e.target.value) {
                return;
            }

            if (topic.baseline === e.target.value && !topic.modifiedBaseline) {
                return;
            }

            if (!topic.baselineId) {
                return;
            }

            store.updateBaselineFromTestRun(topic.baselineId, e.target.value);
        };

        return (
            <div>
                <TextArea
                    tabIndex={tabIndex}
                    defaultValue={topic.modifiedBaseline ?? topic.baseline}
                    onFocus={focusHandler}
                    onBlur={blurHandler}
                    autoSize
                />
            </div>
        );
    };

    const getBaselineFuzzyCellDisplayContent = (topic: TestRunTopic) => {
        if (topic.modifiedBaselineFuzzy) {
            return (
                <span style={{ whiteSpace: 'pre-wrap' }}>
                    <strong>Original value: </strong>
                    {topic.baselineFuzzy}
                    <br />
                    <strong>New value: </strong>
                    {topic.modifiedBaselineFuzzy}
                </span>
            );
        }

        return (
            <span style={{ whiteSpace: 'pre-wrap' }}>
                {topic.baselineFuzzy != null ? `${topic.baselineFuzzy}%` : 'N/A'}
            </span>
        );
    };

    const getBaselineFuzzyCellContent = (topic: TestRunTopic, tabIndex: number, isLast: boolean) => {
        if (!isEditModeOn) {
            return getBaselineFuzzyCellDisplayContent(topic);
        }

        const focusHandler = isLast
            ? (e: React.FocusEvent<HTMLInputElement>) => handleFocus(e, topic.packageId ?? topic.packageName)
            : undefined;

        const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
            const numericValue = parseInt(e.target.value, 10);

            if (isNaN(numericValue)) {
                return;
            }
            if (topic.modifiedBaselineFuzzy === numericValue) {
                return;
            }

            if (topic.baselineFuzzy === numericValue && !topic.modifiedBaselineFuzzy) {
                return;
            }

            if (!topic.baselineId) {
                return;
            }

            store.updateBaselineFuzzyFromTestRun(topic.baselineId, numericValue);
        };

        return (
            <div>
                <InputNumber
                    tabIndex={tabIndex}
                    defaultValue={topic.modifiedBaselineFuzzy ?? topic.baselineFuzzy}
                    onFocus={focusHandler}
                    onBlur={blurHandler}
                    min={0}
                    max={100}
                />
            </div>
        );
    };

    const runContentForPackage = (packageName: string, packageId: string | null) => {
        if (!store.currentTestRunResults) {
            return null;
        }

        const packageIndex = packageListItems.findIndex(p => p.packageId === packageId);

        const columns: ColumnType<TestRunTopic>[] = [
            {
                title: 'Topic',
                dataIndex: 'name',
                key: 'name',
                width: '25%'
            },
            {
                title: 'Baseline',
                dataIndex: 'baseline',
                key: 'baseline',
                width: '35%',
                render: (_: string, topic: TestRunTopic, index: number) =>
                    getBaselineCellContent(topic, topics.length * packageIndex + index + 1, index === topics.length - 1)
            },
            {
                title: 'Result',
                dataIndex: 'result',
                key: 'result',
                width: '40%',
                render: (result: string, record: TestRunTopic) => {
                    const color = record.fuzzy >= record.baselineFuzzy ? 'black' : 'red';
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ color: color, whiteSpace: 'pre-wrap', flex: '1 1' }}>{result}</div>
                            {getRunResultButtons(record)}
                        </div>
                    );
                }
            },
            {
                title: 'Baseline Fuzzy %',
                dataIndex: 'baselineFuzzy',
                key: 'baselineFuzzy',
                width: 140,
                render: (_: string, topic: TestRunTopic, index: number) =>
                    getBaselineFuzzyCellContent(
                        topic,
                        topics.length * packageIndex + index + 1,
                        index === topics.length - 1
                    )
            },
            {
                title: 'Result Fuzzy %',
                dataIndex: 'fuzzy',
                key: 'fuzzy',
                width: 125,
                render: (fuzzy: number) => (fuzzy != null ? `${fuzzy}%` : 'N/A')
            }
        ];

        let topics: TestRunTopic[];

        // If packageId is present, use it to filter topics, otherwise use packageName. For backwards compatibility.
        const allResultsHavePackageId = store.currentTestRunResults.topics.every(t => t.packageId != null);
        if (allResultsHavePackageId && packageId != null) {
            topics = store.currentTestRunResults.topics
                .filter(t => t.packageId === packageId)
                .sort((a, b) => a.name.localeCompare(b.name));
        } else {
            topics = store.currentTestRunResults.topics
                .filter(t => t.packageName === packageName)
                .sort((a, b) => a.name.localeCompare(b.name));
        }

        return (
            <Table
                tableLayout="fixed"
                columns={columns}
                dataSource={topics}
                pagination={false}
                rowKey={(record: TestRunTopic) => record.packageName + record.name}
            />
        );
    };

    const getRunPanelHeader = (packageName: string) => {
        if (!store.currentTestRunResults) {
            return packageName;
        }

        const hasFailedtopics = store.currentTestRunResults.topics
            .filter(t => t.packageName === packageName)
            .some(t => t.fuzzy < t.baselineFuzzy);

        return <span style={{ fontWeight: 500, color: hasFailedtopics ? 'red' : undefined }}>{packageName}</span>;
    };

    const runsContent = () => {
        const coveragePercentage = store.baselineCoveragePercentage;

        return (
            <>
                {coveragePercentage === 0 && (
                    <Alert
                        className="baseline-coverage-warning"
                        style={{ marginBottom: 16 }}
                        message="No baselines configured"
                        description="No baselines configured. Please add baselines to validate test results."
                        type="error"
                        showIcon
                        icon={<WarningOutlined />}
                    />
                )}
                {coveragePercentage > 0 && coveragePercentage < 80 && (
                    <Alert
                        className="baseline-coverage-warning"
                        style={{ marginBottom: 16 }}
                        message="Low baseline coverage"
                        description="High number of empty baselines detected. Review recommended."
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                    />
                )}
                {store.currentTestRunResults?.error && (
                    <Alert
                        style={{ marginBottom: 24 }}
                        message="Error occurred during test project run"
                        description={store.currentTestRunResults.error}
                        type="error"
                        showIcon
                    />
                )}
                <Collapse
                    className="collapse-with-sticky-header darker"
                    activeKey={openPanelKeys}
                    onChange={(keys: string[]) => setOpenPanelKeys(keys)}
                >
                    {packageListItems.map(p => (
                        <Collapse.Panel
                            key={p.packageId ?? p.packageName}
                            header={getRunPanelHeader(p.packageName)}
                            extra={
                                <>
                                    {store.packageIdsWithEmptyBaseline.has(p.packageId ?? p.packageName) && (
                                        <Tag color="orange" style={{ marginRight: 100 }}>
                                            <WarningOutlined /> Empty baseline
                                        </Tag>
                                    )}
                                    <TestProjectPackageActions
                                        packageId={p.packageId}
                                        packageName={p.packageName}
                                        store={store}
                                    />
                                </>
                            }
                        >
                            {runContentForPackage(p.packageName, p.packageId)}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            </>
        );
    };

    return (
        <Layout className="screen-size test-project-dashboard" style={{ ...{ height: '100%', background: 'white' } }}>
            <LayoutHeader
                title={getTitle()}
                subtitle={Utils.getSubtitle(store.currentProject)}
                buttons={[
                    <Button
                        key="test-projects-baselines-go-to-list"
                        data-id="button-go-to-list"
                        className="light"
                        size="large"
                        onClick={store.goBackToList}
                    >
                        Go to list
                    </Button>,
                    <Button
                        key="test-projects-baselines-go-to-dashboard"
                        data-id="button-go-to-dashboard"
                        className="light"
                        size="large"
                        onClick={store.goToDashboard}
                    >
                        Go to dashboard
                    </Button>,
                    renderBatchReparseButton()
                ]}
            />
            <Layout>
                <div className="test-project-actions-wrapper">
                    <div className="test-projects-actions">
                        <Button style={{ margin: '8px 0' }} onClick={() => setIsEditModeOn(prev => !prev)}>
                            {isEditModeOn ? 'Disable Baseline Edit' : 'Enable Baseline Edit'}
                        </Button>
                    </div>

                    <div className="test-project-actions-container">
                        <TestProjectSortingActions
                            fields={Constants.PACKAGE_SORT_FIELDS}
                            fieldName={packageSortField}
                            setFieldName={setPackageSortField}
                            setSortingDirection={setPackageSortDirection}
                            sortingDirection={packageSortDirection}
                            storageKey={Constants.PACKAGE_SORT_DIRECTION_STORAGE_KEY}
                        />
                        <div className="filter-container">
                            <span>Filter:</span>
                            <Select
                                value={resultFilter}
                                onChange={(value: 'all' | 'failed') => setResultFilter(value)}
                                style={{ width: 120 }}
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'failed', label: 'Failed' }
                                ]}
                            />
                        </div>
                        <div className="documents-stats">
                            <div className="baseline-coverage">
                                Baseline coverage: {store.documentsWithBaselineCount}/
                                {store.currentTestRunResults?.documentsCount ?? store.documentsProcessedCount} (
                                {store.baselineCoveragePercentage}%)
                            </div>
                            {store.currentTestRunResults?.documentsCount != null && (
                                <div className="documents-count">
                                    Processed: {store.documentsProcessedCount}/
                                    {store.currentTestRunResults.documentsCount} documents
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Layout.Content style={{ maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
                    {store.testResultIsLoading ? loadingContent : runsContent()}
                </Layout.Content>
            </Layout>
        </Layout>
    );
};

export default observer(TestProjectRunResultList);
